import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { map, take } from 'rxjs/operators';
import { RolesConstants } from '@shared/model/roles-constants';
import { CustomerPriceType } from '@shared-features/enums/customer-price-type';
import { Mimetype } from '@shared/enums/mime-type.enum';
import { MediaType } from '@shared/enums/media-type.enum';
import { Media } from '@shared-features/models/media';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { SectionStateStatus } from '@shared/enums/section-state-status.enum';
import { FileWithMedia } from '@shared/utils/file-with-media.util';
import { MediaApiService } from '@shared-features/services/media-api.service';
import { maxFileSize } from '@shared/validators/file-size.validator';
import { ConfigConstant } from '@shared/constants/config.constant';
import { noWhitespaceValidator } from '@shared/validators/form-validators';
import { Observable, combineLatest, of, zip } from 'rxjs';
import { ShipmentStatus } from '@shared-features/enums/shipment-status.enum';
import { Shipment } from '@shared-features/models/shipment';
import { ShipmentsApiService } from '@features/shipments/services/shipments-api.service';
import { ShipmentDeliveryNoteDto } from '@features/shipments/dto/shipment-delivery-note.dto';

@Component({
  selector: 'app-delivery-note-modal',
  templateUrl: './delivery-note-modal.component.html',
  styleUrls: ['./delivery-note-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeliveryNoteModalComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() shipment: Shipment = new Shipment();
  @Output() closeModal = new EventEmitter();
  @Output() reloadComponent = new EventEmitter();
  @Output() notifyShowModal = new EventEmitter();
  mimeType = Mimetype;
  days = [];

  sectionState: SectionStateStatus = SectionStateStatus.Ready;
  form: FormGroup;
  mediaType = MediaType;
  deliveryNotes: FormArray;
  RolesConstants = RolesConstants;
  filesToUploadCache: { [k: number]: File | File[] };
  showReasonField = false;
  showChangeButton = false;
  CustomerPriceType = CustomerPriceType;
  ShipmentStatus = ShipmentStatus;

  constructor(
    private shipmentsApiService: ShipmentsApiService,
    private formBuilder: FormBuilder,
    private mediaApiService: MediaApiService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.notifyShowModal.emit(false);
    this.fillData();
    for (let index = 0; index < 31; index++) {
      this.days.push(Number(index));
    }
    this.changeDetector.markForCheck();

    if (this.shipment.customerPriceType === CustomerPriceType.CONTRACTED) {
      this.form.controls.customerPrice.disable();
      this.showChangeButton = true;
      this.changeDetector.markForCheck();
    }
  }

  initForm(): void {
    this.form = this.formBuilder.group({
      fleetPrice: [this.shipment?.transactions[0].fleetPrice, ...(this.shipment.fleet ? [Validators.min(1)] : [])],
      customerPrice: [this.shipment?.transactions[0].customerPrice, [Validators.min(1)]],
      customerWaitingChargePerDay: [null],
      fleetWaitingCharge: [this.shipment?.transactions[0].fleetWaitingCharge],
      waitingDays: [
        this.shipment.transactions[0].waitingDays
          ? this.shipment.transactions[0].waitingDays
          : this.days[0],
      ],
      customerReturnCharge: [this.shipment?.transactions[0].customerReturnCharge],
      fleetReturnCharge: [this.shipment?.transactions[0].fleetReturnCharge],
      multiDestinationChargePrice: [this.shipment?.transactions[0].multiDestinationChargePrice],
      multiDestinationChargeCost: [this.shipment?.transactions[0].multiDestinationChargeCost],
      weightingFine: [this.shipment?.transactions[0].weightingFine],
      customerClearanceFees: [this.shipment?.transactions[0].customerClearanceFees],
      fleetWeightingFine: [this.shipment?.transactions[0].fleetWeightingFine],
      fleetClearanceFees: [this.shipment?.transactions[0].fleetClearanceFees],
      advancedPayment: [this.shipment?.transactions[0].advancedPayment],
      damageCost: [this.shipment?.transactions[0].damageCost],
      deliveryNoteComment: [this.shipment?.deliveryNoteComment],
      deliveryNotes: this.getDNControls(),
      waitingChargeProfAttachment: this.formBuilder.group({
        id: [this.shipment?.waitingChargeProfAttachment?.id],
        attachment: [
          this.shipment?.waitingChargeProfAttachment
            ? new FileWithMedia(this.shipment?.waitingChargeProfAttachment)
            : null,
        ],
      }),
    });
    this.changeDetector.markForCheck();
  }

  getDNControls(): FormArray {
    if (this.shipment.deliveryNote.length) {
      return this.formBuilder.array(
        this.shipment.deliveryNote.map((deliveryNote) =>
          this.formBuilder.group({
            id: [deliveryNote.id],
            referenceNumber: [deliveryNote.referenceNumber],
            attachment: [
              deliveryNote.attachment ? new FileWithMedia(deliveryNote.attachment) : null,
            ],
          })
        )
      );
    } else {
      return this.formBuilder.array([this.createDNControls()]);
    }
  }

  createDNControls(): FormGroup {
    return this.formBuilder.group({
      id: [null],
      referenceNumber: [null],
      attachment: [null, maxFileSize(ConfigConstant.MAX_FILE_SIZE)],
    });
  }

  addDNControls(): void {
    this.deliveryNotes = this.form.get('deliveryNotes') as FormArray;
    this.deliveryNotes.push(this.createDNControls());
    this.changeDetector.markForCheck();
  }

  onDeleteDNControls(index): void {
    (this.form.get('deliveryNotes') as FormArray).removeAt(index);
    this.changeDetector.markForCheck();
  }

  handleDNFileChange(event: Media, index: number): void {
    (this.form.get('deliveryNotes') as FormArray).controls[index].patchValue({ attachment: event });
    this.changeDetector.markForCheck();
  }

  onChangCustomerPrice(): void {
    this.form.addControl(
      'priceComment',
      new FormControl(null, [Validators.required, noWhitespaceValidator])
    );
    this.showReasonField = true;
    this.showChangeButton = false;
    this.form.controls.customerPrice.enable();
    this.changeDetector.markForCheck();
  }

  onSaveClicked(): void {
    let existAttachmentWithoutReferenceNumber = false;
    let isAnyAttachmentExistToUpload = this.form.value.waitingChargeProfAttachment?.attachment;
    let deliveryNoteIndex = 0;

    this.form.get('deliveryNotes').value.some((deliveryNote, index) => {
      deliveryNoteIndex = index + 1;
      if (deliveryNote.attachment) {
        if (!deliveryNote.referenceNumber) {
          existAttachmentWithoutReferenceNumber = true;
          return true;
        }
        isAnyAttachmentExistToUpload = true;
      }
    });

    if (existAttachmentWithoutReferenceNumber) {
      this.asyncFeedbackService.showFailureMessage(
        this.translationsList.ErrorMessages.EnterIdForDeliveryNote + '(' + deliveryNoteIndex + ')'
      );
      return;
    } else if (isAnyAttachmentExistToUpload) {
      this.uploadFiles();
    } else {
      this.saveDeliveryNoteInfo();
    }
  }

  returnZeroIfNullValue(value): any {
    return value || '0';
  }

  onCancelClicked(): void {
    this.form?.reset();
    this.notifyShowModal.emit(false);
    this.closeModal.emit();
  }

  // private methods

  private prepareFilesForUpload(): { [k: number]: File | File[] } {
    this.filesToUploadCache = this.form.value.deliveryNotes.reduce((filesMap, deliveryNote, i) => {
      if (
        !deliveryNote?.attachment ||
        (deliveryNote.attachment && deliveryNote.attachment.media instanceof Media)
      ) {
        return filesMap;
      }
      filesMap[i] = deliveryNote.attachment;
      return filesMap;
    }, {});
    return this.filesToUploadCache;
  }

  private uploadDeliveryNote(): Observable<any> {
    this.prepareFilesForUpload();
    const files = Object.values(this.filesToUploadCache) as File[];

    return !files.length
      ? of(null)
      : zip(
          ...files.map((file) =>
            this.mediaApiService.uploadMedia(
              file,
              file?.type?.startsWith('image/') ? MediaType.IMAGE : MediaType.DOCUMENT
            )
          )
        ).pipe(
          take(1),
          map((filesList: Media[][]) => {
            return this.form.value.deliveryNotes.map((note, i) => {
              if (this.filesToUploadCache[i]) {
                const index = files.indexOf(this.filesToUploadCache[i] as File);
                const file = filesList[index][0] as Media;
                const formGroup = (this.form.controls.deliveryNotes as FormArray).controls[i];
                formGroup.patchValue({ ...formGroup.value, attachment: new FileWithMedia(file) });
                formGroup.updateValueAndValidity();
                return file instanceof Media ? file : null;
              }
              return note.attachment instanceof FileWithMedia
                ? note.attachment.media
                : note.attachment;
            });
          })
        );
  }

  private uploadWaitingChargeProof(): Observable<any> {
    const file = this.form.value.waitingChargeProfAttachment;
    return !file.attachment
      ? of(null)
      : this.mediaApiService.uploadMedia(file.attachment, file.attachment?.type?.startsWith('image/') ? MediaType.IMAGE : MediaType.DOCUMENT).pipe(
          map((media: Media[]) => {
            this.form.controls.waitingChargeProfAttachment.setValue({
              ...file,
              id: media[0].id,
              attachment: new FileWithMedia(file),
            });
          })
        );
  }

  private uploadFiles(): void {
    this.load(combineLatest([this.uploadDeliveryNote(), this.uploadWaitingChargeProof()]), {
      isLoadingTransparent: true,
    }).subscribe(() => {
      Promise.resolve().then(() => this.saveDeliveryNoteInfo());
    });
  }

  private saveDeliveryNoteInfo(): void {
    const {
      waitingChargePrice,
      deliveryNoteComment,
      deliveryNotes,
      waitingChargeProfAttachment,
      ...form
    } = this.form.value;
    const shipment: Shipment = Object.assign(new Shipment(), { ...this.shipment }, {
      transactions: [Object.assign({ ...this.shipment.transactions[0] }, form)],
      deliveryNoteComment: deliveryNoteComment ? deliveryNoteComment : '',
      priceComment: this.form.get('priceComment')?.value
        ? this.form.get('priceComment')?.value
        : null,
      waitingChargeProfId: waitingChargeProfAttachment.attachment
        ? waitingChargeProfAttachment.id
        : null,
      deliveryNote: deliveryNotes.map((note, i) => ({
        ...note,
        attachment:
          note.attachment instanceof FileWithMedia ? note.attachment.media : note.attachment,
      })),
    });
    this.shipment.priceComment = shipment.priceComment;

    const shipmentDeliveryNoteDto = new ShipmentDeliveryNoteDto(shipment);

    this.load(
      this.shipmentsApiService.updateShipmentPriceAndDeliveryNote(shipmentDeliveryNoteDto),
      {
        isLoadingTransparent: true,
        fetchOnce: true,
      }
    ).subscribe(() => {
      this.showSuccessMessage('SuccessMessages.DeliveryNoteChangedSuccessfully');
      this.shipment = Object.assign(this.shipment, shipment);
      this.closeModal.emit();
      this.reloadComponent.emit();
      this.changeDetector.markForCheck();
    });
  }

  fillData() {
    this.initForm();
    this.form.controls.customerWaitingChargePerDay.setValue(
      this.shipment?.transactions[0].waitingDays
        ? this.shipment?.transactions[0].customerWaitingChargePerDay
        : this.shipment?.customer?.waitingChargePerDay
    );
    this.notifyShowModal.emit(true);
    this.sectionState = SectionStateStatus.Ready;
    this.changeDetector.markForCheck();
  }
}
